// ═══ 인보이스 페이지 상태/핸들러 훅 — Invoice.js에서 분리 ═══
// 50+ useState/useEffect/useRef + 모든 핸들러 함수를 캡슐화. 부모는 ctx(c)와 회사 필터 상태만 사용.
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { API, carsUrl, authFetch } from '../utils/api';
import useSettingsFile from '../utils/useSettingsFile';
import { getYearFromVin } from '../utils/vinDecoder';
import { printInvoice as invoicePrintUtil } from '../utils/invoicePrint';
import { pickPriceStr as _pickPriceStr, makeRow as _makeRow, makeEmptyRow as _emptyRow, makeToInvCur, calcTotals, filterInitCars } from '../utils/invoiceCalc';
import { compressFiles } from '../utils/imageCompress';
import { applyOcrResult as _applyOcrResultUtil, handleScreenshot as _handleScreenshotUtil, handleTextPaste as _handleTextPasteUtil } from '../utils/invoiceOcrUtils';
import { saveInvoiceDraft as _saveInvoiceDraftUtil, restoreInvoiceDraft as _restoreInvoiceDraftUtil } from '../utils/invoiceDraftPersistence';
import { forceSend as _forceSendUtil } from '../utils/invoiceSubmit';

export function useInvoiceState(opts) {
  var showToast = opts.showToast;
  var user = opts.user;
  var location = useLocation();
  // location.state는 브라우저 reload(=업체 전환) 후에도 history.state로 보존되므로,
  // 저장된 initCars가 현재 활성 업체와 다른 회사 차량이면 폐기 (cross-tenant prefill 차단)
  var _curUserCid = (user && (user.activeCompanyId || user.companyId)) || 0;
  var _rawInitCars = location.state && location.state.initCars ? location.state.initCars : [];
  var _isDocAgency2 = user && user.role === 'doc_agency';
  var initCars = filterInitCars(_rawInitCars, _curUserCid, _isDocAgency2);
  var isAdmin=user&&(user.role==='관리자'||user.role==='admin');
  var isDocAgency=user&&user.role==='doc_agency';
  var showCompanyFilter=isAdmin||isDocAgency;
  var[filterCompanyId,setFilterCompanyId]=useState((isAdmin||isDocAgency)&&user?(user.activeCompanyId||user.companyId||''):'');
  var[cars,setCars]=useState([]);var[buyers,setBuyers]=useState([]);
  var[shipCo,setShipCo]=useState({companyName:'AUTODEX',addr:'',tel:'',fax:'',bizNo:''});
  var[selBuyer,setSelBuyer]=useState(null);
  var[invoiceNo,setInvoiceNo]=useState('');var[invoiceDate,setInvoiceDate]=useState(new Date().toISOString().slice(0,10));
  var[consignee,setConsignee]=useState('');var[vesselName,setVesselName]=useState('');
  var[fromPort,setFromPort]=useState('INCHEON, KOREA');var[toPort,setToPort]=useState('');
  var[departDate,setDepartDate]=useState('');
  var[bkNo,setBkNo]=useState('');var[containerNo,setContainerNo]=useState('');var[sealNo,setSealNo]=useState('');var[entryPoint,setEntryPoint]=useState('');
  var[freightCurrency,setFreightCurrency]=useState('KRW');
  var[insuranceCurrency,setInsuranceCurrency]=useState('KRW');
  // 순수 함수로 분리됨 → utils/invoiceCalc.js
  // pickPriceStr / mkRow는 currency state에 의존하므로 closure로 wrap
  var pickPriceStr = _pickPriceStr;
  var mkRow = function(c) {
    var _invCur = typeof currency !== 'undefined' && currency ? currency : 'KRW';
    return _makeRow(c, _invCur);
  };
  var[carRows,setCarRows]=useState(function(){return initCars&&initCars.length?initCars.map(mkRow):[mkRow(null)]});
  var[toEmail,setToEmail]=useState('');var[sending,setSending]=useState(false);var[sent,setSent]=useState(false);
  var[shippingType,setShippingType]=useState('roro');
  var[currency,setCurrency]=useState('KRW');
  var[incoterms,setIncoterms]=useState('FOB');
  var[freight,setFreight]=useState('');
  var[insurance,setInsurance]=useState('');
  var[carSearch,setCarSearch]=useState('');
  var[carSearchResults,setCarSearchResults]=useState([]);
  var[carSearchShow,setCarSearchShow]=useState(false);
  var[carSearchTimer,setCarSearchTimer]=useState(null);
  var doCarSearch=function(q){
    if(carSearchTimer)clearTimeout(carSearchTimer);
    if(!q||q.length<2){setCarSearchResults([]);setCarSearchShow(false);return;}
    var t=setTimeout(function(){
      authFetch(API+'/cars/search?q='+encodeURIComponent(q)).then(function(r){return r.json()}).then(function(d){setCarSearchResults(d);setCarSearchShow(d.length>0)}).catch(function(){});
    },300);
    setCarSearchTimer(t);
  };
  var[exchangeRate,setExchangeRate]=useState(0);
  var[rateLoading,setRateLoading]=useState(false);
  var[rateEditing,setRateEditing]=useState(false);
  var CURRENCIES=[{code:'USD',symbol:'$'},{code:'KRW',symbol:'₩'},{code:'AED',symbol:'د.إ'},{code:'EUR',symbol:'€'}];
  var INCOTERMS=['FOB','CIF','CFR','EXW','DDP','DAP'];
  var curSymbol=CURRENCIES.find(function(x){return x.code===currency})||CURRENCIES[0];
  // 환율 자동 조회 (USD→KRW 기준 환율 항상 보유)
  var[usdToKrw,setUsdToKrw]=useState(0);
  var fetchRate=function(cur){
    if(cur==='KRW'){setExchangeRate(1);}else{setRateLoading(true);}
    // USD→KRW 환율은 항상 조회 (CFR/CIF 운임 변환용)
    fetch('https://open.er-api.com/v6/latest/USD').then(function(r){return r.json()}).then(function(d){
      if(d.rates&&d.rates.KRW){setUsdToKrw(Math.round(d.rates.KRW*100)/100)}
      if(cur!=='KRW'&&d.rates&&d.rates.KRW){
        if(cur==='USD'){setExchangeRate(Math.round(d.rates.KRW*100)/100)}
      }
      setRateLoading(false);
    }).catch(function(){setRateLoading(false)});
    // USD 외 통화는 별도 조회
    if(cur!=='KRW'&&cur!=='USD'){
      fetch('https://open.er-api.com/v6/latest/'+cur).then(function(r){return r.json()}).then(function(d){
        if(d.rates&&d.rates.KRW){setExchangeRate(Math.round(d.rates.KRW*100)/100)}
      }).catch(function(){});
    }
  };
  useEffect(function(){fetchRate(currency)},[currency]);
  // Method 2: 인보이스 통화 변경 시 모든 행 재동기화 (단일 통화로만 표시)
  // 이미 같은 통화 행은 건드리지 않음 → loadInvoice 직후 사용자 커스텀 가격 보존
  useEffect(function(){
    setCarRows(function(prev){return prev.map(function(r){
      if(r.priceCurrency===currency)return r;
      if(!r.carId)return {...r,priceCurrency:currency};
      return {...r,price:pickPriceStr(r,currency),priceCurrency:currency};
    })});
  },[currency]);
  // toForeign/toKrw 제거 — 미사용 (toInvCur가 모든 통화 변환 담당)
  var[shoringFiles,setShoringFiles]=useState([]);
  var[uploadedDocs,setUploadedDocs]=useState([]);
  var[sendWarnings,setSendWarnings]=useState([]);
  var[savedInvoiceId,setSavedInvoiceId]=useState(null);
  var[invoiceStatus,setInvoiceStatus]=useState('draft');
  // Phase 1.5 #14 파일럿: 낙관적 락 버전 echo. 불러올 때 저장, PUT 시 전송, 409 시 새로고침 안내
  var[invoiceVersion,setInvoiceVersion]=useState(null);
  var[savedList,setSavedList]=useState([]);
  var[confirmAction,setConfirmAction]=useState(null);
  var[carDocs,setCarDocs]=useState({});
  // 🔴 SECURITY (Wave 1B, 2026-04-29): signatureUrl 정적 URL → useSettingsFile blob 마이그
  // filterCompanyId 또는 user 활성 회사 기준 — 변경 시 자동 재fetch + 이전 blob revoke.
  var _sigCid=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;
  var _sigFile=useSettingsFile('signature',_sigCid);
  var signatureUrl=_sigFile.url||'';
  var[ssMode,setSsMode]=useState(false);
  var[ssLoading,setSsLoading]=useState(false);
  var[ssResult,setSsResult]=useState(null);
  var[ssImageUrl,setSsImageUrl]=useState('');
  var[ssPreview,setSsPreview]=useState(false);
  var[ssPinned,setSsPinned]=useState(false);
  var[ssEdit,setSsEdit]=useState({});
  var[ssZoom,setSsZoom]=useState(1);
  var[ssDrag,setSsDrag]=useState({dragging:false,startX:0,startY:0,offX:0,offY:0,curX:0,curY:0});
  // OCR 핸들러 — utils/invoiceOcrUtils.js 로 분리 (Phase 2 시동, 2026-05-08)
  // deps 로 setter 들 + buyers/mkRow/showToast 주입
  var _ocrDeps = function() { return {
    setSsLoading, setSsResult, setContainerNo, setSealNo, setEntryPoint, setBkNo,
    setVesselName, setToPort, setFromPort, setSelBuyer, setConsignee, setCarRows,
    setSsImageUrl, ssImageUrl,
    buyers: buyers, mkRow: mkRow, showToast: showToast
  };};
  var handleScreenshot = function(file) { _handleScreenshotUtil(file, _ocrDeps()); };
  var handleTextPaste = function(text) { _handleTextPasteUtil(text, _ocrDeps()); };
  var ls={display:'block',fontSize:11,fontWeight:600,color:'#64748b',marginBottom:4,marginTop:10};
  var is={width:'100%',padding:'7px 10px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:12,outline:'none',background:'#fff'};
  var is2={padding:'5px 8px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:11,outline:'none',background:'#fff',width:'100%'};

  useEffect(function(){
    authFetch(carsUrl(user)).then(function(r){return r.json()}).then(function(d){setCars(d);d.forEach(function(c){authFetch(API+'/cars/'+c.id+'/docs').then(function(r2){return r2.json()}).then(function(docs){setCarDocs(function(prev){var n=Object.assign({},prev);n[c.id]=docs;return n})}).catch(function(){})})}).catch(function(){});
    authFetch(API+'/buyers').then(function(r){return r.json()}).then(function(d){setBuyers(d)}).catch(function(){});
    // 🔴 Wave 1B: signature 로딩은 useSettingsFile hook 으로 자동 처리 (위 _sigFile)
    // 업체 정보 로드
    var cid=filterCompanyId||(user&&(user.activeCompanyId||user.companyId))||0;
    authFetch(API+'/settings/company'+(cid?'?companyId='+cid:'')).then(function(r){return r.json()}).then(function(d){if(d.companyName)setShipCo(d)}).catch(function(){});
    // 저장된 인보이스 목록 로드
    var invUrl=API+'/invoices';if(showCompanyFilter&&filterCompanyId)invUrl+='?companyId='+filterCompanyId;
    authFetch(invUrl).then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setSavedList(d)}).catch(function(){});
    // 마운트시 legacy 글로벌 키 정리
    try{sessionStorage.removeItem('autodoc_invoice_temp')}catch(e){}
    setInvoiceNo('INV-'+new Date().getFullYear()+'-'+String(Math.floor(Math.random()*9000)+1000));
    // 사이드탭 전환 후 복귀: initCars가 비어있으면 회사별 드래프트 복원 (있으면 initCars 우선)
    if(!initCars||initCars.length===0){
      var _cidR=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;
      if(_cidR)restoreInvoiceDraft(_cidR);
    }
  },[]);

  // 언마운트 시 현재 폼을 회사별 sessionStorage에 자동 저장 (사이드탭 전환 후 복귀 보존)
  // ref로 최신 closure를 capture — cleanup이 stale state를 보지 않도록
  var unmountSaveRef=useRef(null);
  unmountSaveRef.current=function(){
    var hasContent=carRows.some(function(r){return r.carNo||r.vin||r.price||r.weight});
    if(!hasContent)return; // 빈 폼이면 저장도 삭제도 하지 않음 (기존 드래프트 보존)
    var cid=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;
    if(cid)saveInvoiceDraft(cid);
  };
  useEffect(function(){return function(){if(unmountSaveRef.current)unmountSaveRef.current()}},[]);

  // 업체 전환 시 filterCompanyId 자동 동기화 (격리 — 옛 회사의 stale state 차단)
  useEffect(function(){
    if(!user)return;
    var newCid=(isAdmin||isDocAgency)?(user.activeCompanyId||user.companyId||''):'';
    setFilterCompanyId(newCid);
  },[user&&user.activeCompanyId,user&&user.companyId]);

  // 드래프트 persistence — utils/invoiceDraftPersistence.js 로 분리 (Phase 2 시동, 2026-05-08)
  var saveInvoiceDraft = function(cid) {
    _saveInvoiceDraftUtil(cid, {
      carRows: carRows, selBuyer: selBuyer, savedInvoiceId: savedInvoiceId,
      consignee: consignee, vesselName: vesselName, fromPort: fromPort, toPort: toPort, departDate: departDate,
      bkNo: bkNo, containerNo: containerNo, sealNo: sealNo, entryPoint: entryPoint,
      currency: currency, exchangeRate: exchangeRate, incoterms: incoterms,
      freight: freight, insurance: insurance, freightCurrency: freightCurrency, insuranceCurrency: insuranceCurrency,
      shippingType: shippingType, toEmail: toEmail, invoiceNo: invoiceNo, invoiceDate: invoiceDate
    });
  };
  var restoreInvoiceDraft = function(cid) {
    return _restoreInvoiceDraftUtil(cid, {
      setCarRows: setCarRows, setSelBuyer: setSelBuyer, setConsignee: setConsignee, setVesselName: setVesselName,
      setFromPort: setFromPort, setToPort: setToPort, setDepartDate: setDepartDate, setBkNo: setBkNo,
      setContainerNo: setContainerNo, setSealNo: setSealNo, setEntryPoint: setEntryPoint,
      setCurrency: setCurrency, setExchangeRate: setExchangeRate, setIncoterms: setIncoterms,
      setFreight: setFreight, setInsurance: setInsurance,
      setFreightCurrency: setFreightCurrency, setInsuranceCurrency: setInsuranceCurrency,
      setShippingType: setShippingType, setToEmail: setToEmail,
      setInvoiceNo: setInvoiceNo, setInvoiceDate: setInvoiceDate, setSavedInvoiceId: setSavedInvoiceId,
      loadInvoice: loadInvoice, showToast: showToast
    });
  };

  // filterCompanyId 변경 시 인보이스 목록 + Shipper 정보 + 차량 목록 재로드
  // 마운트 시점에는 stale state 클리어를 skip — 마운트 useEffect(L143)와 initCars 폼 prefill을 보존
  var prevFilterCompanyIdRef=useRef(filterCompanyId);
  useEffect(function(){
    if(!showCompanyFilter||!filterCompanyId)return;
    var isRealChange=prevFilterCompanyIdRef.current!==filterCompanyId;
    prevFilterCompanyIdRef.current=filterCompanyId;
    if(isRealChange){
      // 격리: 회사가 실제로 바뀌었을 때만 옛 회사 차량/인보이스 즉시 비우기
      setCars([]);
      setSavedList([]);
      setShoringFiles([]);
      setUploadedDocs([]);
      setSavedInvoiceId(null);
      // 새 회사 드래프트 복원 시도, 없으면 빈 폼
      var restored=restoreInvoiceDraft(filterCompanyId);
      if(!restored)setCarRows([mkRow(null)]);
    }
    var invUrl=API+'/invoices?companyId='+filterCompanyId;
    authFetch(invUrl).then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setSavedList(d)}).catch(function(){});
    authFetch(API+'/settings/company?companyId='+filterCompanyId).then(function(r){return r.json()}).then(function(d){if(d.companyName)setShipCo(d)}).catch(function(){});
    authFetch(API+'/cars?companyId='+filterCompanyId).then(function(r){return r.json()}).then(function(d){setCars(d)}).catch(function(){});
  },[filterCompanyId]);

  var resetForm=function(){setCarRows([mkRow(null)]);setSelBuyer(null);setConsignee('');setVesselName('');setToPort('');setDepartDate('');setBkNo('');setContainerNo('');setSealNo('');setEntryPoint('');setInvoiceNo('INV-'+new Date().getFullYear()+'-'+String(Math.floor(Math.random()*9000)+1000));setShoringFiles([]);setUploadedDocs([])};

  // 임시저장(draft) 인보이스 삭제
  //  - invoice_status==='draft' 만 허용 (발송된 인보이스는 삭제 불가)
  //  - 현재 편집 중인 인보이스 삭제 시 폼 리셋
  //  - 삭제 후 savedList 재조회
  var deleteDraft=function(id){
    if(!id)return;
    var target=(savedList||[]).find(function(inv){return inv.id===id});
    if(!target){showToast('인보이스를 찾을 수 없습니다','err');return;}
    // 드롭다운 라벨과 일관: sent/ready가 아니면 임시저장으로 간주 (invoice_status NULL 포함)
    if(target.invoice_status==='sent'||target.invoice_status==='ready'){showToast('임시저장(draft) 상태만 삭제 가능합니다','err');return;}
    setConfirmAction({
      title:'임시저장 삭제',
      msg:'#'+id+' 임시저장 인보이스를 삭제할까요? 복구할 수 없습니다.',
      type:'delete',
      fn:function(){
        authFetch(API+'/invoices/'+id,{method:'DELETE'}).then(function(r){return r.json()}).then(function(d){
          if(d.success){
            showToast('삭제 완료');
            if(savedInvoiceId===id){
              setSavedInvoiceId(null);
              resetForm();
            }
            authFetch(API+'/invoices').then(function(r){return r.json()}).then(function(d2){if(Array.isArray(d2))setSavedList(d2)}).catch(function(){});
          } else {
            showToast('삭제 실패: '+(d.error||''),'err');
          }
        }).catch(function(){showToast('서버 오류','err')});
      }
    });
  };
  var handleFilesAdded = async function(files){
    if(!files||files.length===0)return;
    var invId=savedInvoiceId;
    if(!invId){
      // 가드: 신규 인보이스 + 차량 미입력 상태에서 첨부 시도하면 saveInvoiceToDb가 'DB에 매칭되는 차량이 없습니다'를 띄움 → 사용자 혼란.
      // 차량 행 매칭 가능 여부를 먼저 검사해 명시적 안내 후 early return.
      var matchedCount=0;
      for(var ri=0;ri<carRows.length;ri++){var rr=carRows[ri];if(cars.find(function(cc){return cc.vin===rr.vin||cc.carNo===rr.carNo})){matchedCount++}}
      if(matchedCount===0){showToast('차량을 먼저 추가한 뒤 첨부파일을 업로드해주세요','err');return;}
      invId=await saveInvoiceToDb();
      if(!invId){setShoringFiles(function(prev){return prev.concat(Array.from(files))});return}
      setInvoiceStatus('draft');
      authFetch(API+'/invoices').then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setSavedList(d)}).catch(function(){});
    }
    var compressed=await compressFiles(files);
    var fd=new FormData();fd.append('docType','shoring_photo');
    compressed.forEach(function(f){fd.append('files',f)});
    try{
      var resp=await authFetch(API+'/invoices/'+invId+'/upload',{method:'POST',body:fd});
      var data=await resp.json();
      if(data.success){
        var invResp=await authFetch(API+'/invoices/'+invId);
        var inv=await invResp.json();
        if(inv&&inv.documents)setUploadedDocs(inv.documents);
        showToast('\u2705 \uCCA8\uBD80\uD30C\uC77C '+files.length+'\uAC74 \uC5C5\uB85C\uB4DC \uC644\uB8CC');
      }else{showToast('\uCCA8\uBD80\uD30C\uC77C \uC5C5\uB85C\uB4DC \uC2E4\uD328','err')}
    }catch(e){showToast('\uCCA8\uBD80\uD30C\uC77C \uC5C5\uB85C\uB4DC \uC2E4\uD328','err')}
  };
  var addRow=function(){setCarRows(function(p){return p.concat(_emptyRow())});};
  var removeRow=function(id){setCarRows(function(p){return p.filter(function(r){return r.id!==id})});};
  var updateRow=function(id,field,val){setCarRows(function(p){return p.map(function(r){return r.id===id?{...r,[field]:val}:r})});};
  var onCarSelect=function(rowId,carOrId){
    // 검색 결과 객체를 직접 받거나(우선) 또는 id로 cars state에서 lookup(폴백)
    var c=(carOrId&&typeof carOrId==='object')?carOrId:cars.find(function(x){return x.id===parseInt(carOrId)});
    if(!c)return;
    var krw=c.buyPrice||0;
    var _vy2=getYearFromVin(c.vin,c.regDate?parseInt(c.regDate.slice(0,4)):null);var _yr2=_vy2?String(_vy2):(c.regDate?c.regDate.slice(0,4):'');
    var cache={_carInvAmt:c.invoice_amount>0?c.invoice_amount:0,_carUsd:c.sell_price_usd>0?c.sell_price_usd:0,_carKrw:krw};
    var newPrice=pickPriceStr(cache,currency);
    setCarRows(function(p){return p.map(function(r){return r.id===rowId?{...r,carId:c.id,carNo:c.carNo||'',carType:c.carType||'',year:_yr2,vin:c.vin||'',priceKrw:krw,_carInvAmt:cache._carInvAmt,_carUsd:cache._carUsd,_carKrw:cache._carKrw,price:newPrice,priceCurrency:currency}:r})});
    if(c.buyerId&&!selBuyer){var b=buyers.find(function(x){return x.id===c.buyerId});if(b)setSelBuyer(b);}
  };
  // onPhotoChange/onPhotosChange/removePhoto 제거 — 자식 컴포넌트에서 미사용
  // 통화 변환 + 합계 계산 → utils/invoiceCalc.js
  var toInvCur = makeToInvCur(currency, exchangeRate, usdToKrw);
  var _totals = calcTotals(carRows, freight, freightCurrency, insurance, insuranceCurrency, currency, toInvCur);
  var totalAmt = _totals.totalAmt;
  var totalFreightInv = _totals.totalFreightInv;
  var totalInsuranceInv = _totals.totalInsuranceInv;
  var totalWeight = _totals.totalWeight;
  var grandTotal = _totals.grandTotal;

  // printInvoice → utils/invoicePrint.js로 이동. 호출 시점에 현재 ctx 스냅샷을 전달.
  var printInvoice=function(){
    invoicePrintUtil({
      curSymbol:curSymbol,carRows:carRows,selBuyer:selBuyer,consignee:consignee,
      signatureUrl:signatureUrl,shipCo:shipCo,invoiceNo:invoiceNo,invoiceDate:invoiceDate,
      vesselName:vesselName,fromPort:fromPort,toPort:toPort,departDate:departDate,
      bkNo:bkNo,containerNo:containerNo,sealNo:sealNo,entryPoint:entryPoint,
      incoterms:incoterms,currency:currency,exchangeRate:exchangeRate,
      totalAmt:totalAmt,totalFreightInv:totalFreightInv,totalInsuranceInv:totalInsuranceInv,
      totalWeight:totalWeight,grandTotal:grandTotal,toInvCur:toInvCur
    });
  };

  // 인보이스 DB 저장 (공통)
  var[savingDraft,setSavingDraft]=useState(false);
  var saveInvoiceToDb = async function(){
    if(!carRows.length){showToast('차량을 추가하세요','err');return null;}
    // 2026-05-01: 자유 텍스트 흐름 fallback — selBuyer.id 또는 consignee 텍스트 둘 중 하나 있으면 통과
    // 백엔드 invoices-crud.js POST/PUT 가드와 정합 (consigneeName fallback)
    if((!selBuyer||!selBuyer.id) && !(consignee||'').trim()){showToast('바이어 또는 수하인 이름을 입력해주세요','err');return null;}
    var carIds=[];var carFobs=[];var carWeights=[];var carCurrencies=[];
    for(var ri=0;ri<carRows.length;ri++){
      var matched=cars.find(function(c){return c.vin===carRows[ri].vin||c.carNo===carRows[ri].carNo});
      if(matched){carIds.push(matched.id);carFobs.push(parseInt(String(carRows[ri].price).replace(/[^0-9]/g,''))||0);carWeights.push(carRows[ri].weight||'');carCurrencies.push(carRows[ri].priceCurrency||currency);}
    }
    if(carIds.length===0){showToast('DB에 매칭되는 차량이 없습니다','err');return null;}
    // Kyle 2026-05-04 — 필수 5 필드 사전 가드 (백엔드 400 도달 전)
    var hasZeroFob=false;for(var fi=0;fi<carFobs.length;fi++){if(!(carFobs[fi]>0)){hasZeroFob=true;break;}}
    if(hasZeroFob){showToast('모든 차량의 단가를 입력해주세요','err');return null;}
    if(currency!=='KRW' && (parseFloat(exchangeRate)||0)<=0){showToast('외화 인보이스는 환율을 입력해주세요','err');return null;}
    if(!(toPort||'').trim()){showToast('도착항을 입력해주세요','err');return null;}
    // shippingType==='container' 일 때만 컨테이너번호/씰번호 가드 (Kyle 2026-05-05 RORO 면제)
    if(shippingType==='container'){
      if(!(containerNo||'').trim()){showToast('컨테이너번호를 입력해주세요','err');return null;}
      if(!(sealNo||'').trim()){showToast('씰번호를 입력해주세요','err');return null;}
    }
    if(!(entryPoint||'').trim()){showToast('Entry Point 를 입력해주세요','err');return null;}
    // 명시적 companyId 전송 — 다중업체/탭 전환 시 stale activeCompanyId로 인한 wrong-company 저장 방지
    var explicitCid=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;
    var payload={
      buyerId:selBuyer?selBuyer.id:0, fobPrice:totalAmt, currency:currency, paymentTerms:'T/T',
      shippingPort:fromPort, destinationPort:toPort, shipDate:departDate,
      shippingType:shippingType, purchaseType:'individual',
      customsEmail:toEmail, memo:'', carIds:carIds, carFobs:carFobs, carWeights:carWeights, carCurrencies:carCurrencies,
      exchangeRate:exchangeRate, amountForeign:totalAmt, incoterms:incoterms,
      freight:totalFreightInv, insurance:totalInsuranceInv,
      bkNo:bkNo, containerNo:containerNo, sealNo:sealNo, entryPoint:entryPoint,
      consigneeName:consignee||(selBuyer?selBuyer.name:''), consigneeAddr:selBuyer?selBuyer.addr||'':'',
      companyId:explicitCid
    };
    var saveUrl=savedInvoiceId?API+'/invoices/'+savedInvoiceId:API+'/invoices';
    var saveMethod=savedInvoiceId?'PUT':'POST';
    // Phase 1.5 #14: PUT 경로에서 낙관적 락 version echo (현재 불러온 버전 전송)
    if(saveMethod==='PUT'&&invoiceVersion!=null)payload.version=invoiceVersion;
    var saveResp=await authFetch(saveUrl,{method:saveMethod,headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
    // Phase 1.5 #14: 409 Conflict — 다른 사용자가 먼저 저장함. 새로고침 안내.
    if(saveResp.status===409){
      var conflictData=await saveResp.json().catch(function(){return{}});
      showToast('⚠️ '+(conflictData.error||'다른 사용자가 먼저 저장했습니다. 새로고침 후 다시 시도해주세요.'),'err');
      return null;
    }
    var saveData=await saveResp.json();
    if(!saveData.success){showToast('인보이스 저장 실패','err');return null;}
    var resultId=savedInvoiceId||saveData.id;
    setSavedInvoiceId(resultId);
    // Phase 1.5 #14: 저장 성공 후 최신 version 재조회 (이후 편집 시 stale 409 방지)
    if(saveMethod==='PUT'&&invoiceVersion!=null)setInvoiceVersion(invoiceVersion+1);
    // 저장 성공 시 현재 회사 드래프트 캐시 제거 (다음 진입 시 stale 복원 방지)
    try{var _doneCid=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;if(_doneCid)sessionStorage.removeItem('autodoc_invoice_temp_'+_doneCid)}catch(e){}
    return resultId;
  };

  // 임시저장 (DB 저장만, 전송 안 함)
  // 저장된 인보이스 불러오기
  var loadInvoice = function(invId){
    authFetch(API+'/invoices/'+invId).then(function(r){return r.json()}).then(function(inv){
      if(!inv||!inv.id)return;
      setSavedInvoiceId(inv.id);
      setInvoiceStatus(inv.invoice_status||'draft');
      // Phase 1.5 #14: 낙관적 락 버전 저장 (PUT 시 echo)
      setInvoiceVersion(typeof inv.version==='number'?inv.version:(parseInt(inv.version)||null));
      setToPort(inv.destinationPort||'');setFromPort(inv.shippingPort||'INCHEON, KOREA');
      setDepartDate(inv.shipDate||'');setShippingType(inv.shippingType||'roro');
      setCurrency(inv.currency||'USD');setExchangeRate(inv.exchangeRate||0);
      setIncoterms(inv.incoterms||'FOB');
      setBkNo(inv.bk_no||'');setContainerNo(inv.container_no||'');setSealNo(inv.seal_no||'');setEntryPoint(inv.entry_point||'');
      setToEmail(inv.customsEmail||'');
      setConsignee(inv.consigneeName||'');
      if(inv.buyerId){var b=buyers.find(function(x){return x.id===inv.buyerId});if(b)setSelBuyer(b)}
      if(inv.cars&&inv.cars.length>0){
        var _invCur=inv.currency||'USD';
        setCarRows(inv.cars.map(function(c){
          var _vy3=getYearFromVin(c.vin,c.regDate?parseInt(c.regDate.slice(0,4)):null);
          // 차량 메타 (cars state) 에서 _car* 캐시 보강 — 통화 전환 시 재계산용
          var matched=cars.find(function(x){return x.id===(c.carId||0)});
          var _invAmt=matched&&matched.invoice_amount>0?matched.invoice_amount:0;
          var _usd=matched&&matched.sell_price_usd>0?matched.sell_price_usd:0;
          var _krw=matched?matched.buyPrice||0:(c.buyPrice||0);
          return{id:c.carId||Date.now(),carId:c.carId||0,carNo:c.carNo||'',carType:c.carType||'',year:_vy3?String(_vy3):(c.regDate?c.regDate.slice(0,4):''),vin:c.vin||'',price:String(c.fobPrice||0),priceCurrency:c.currency||_invCur,_carInvAmt:_invAmt,_carUsd:_usd,_carKrw:_krw,weight:c.weight||'',photo:null,photoUrl:''};
        }));
      }
      setShoringFiles([]);
      setUploadedDocs(Array.isArray(inv.documents)?inv.documents:[]);
    }).catch(function(){});
  };

  var saveDraft = async function(){
    setSavingDraft(true);
    try {
      var invId=await saveInvoiceToDb();
      if(invId){
        setInvoiceStatus('draft');
        authFetch(API+'/invoices').then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setSavedList(d)}).catch(function(){});
        if(shoringFiles.length>0){
          var fd1=new FormData();fd1.append('docType','shoring_photo');
          var compressed1=await compressFiles(shoringFiles);
          compressed1.forEach(function(f){fd1.append('files',f);});
          await authFetch(API+'/invoices/'+invId+'/upload',{method:'POST',body:fd1});
        }
      }
    } catch(e){}
    setSavingDraft(false);
  };

  // 인보이스 저장 → 추가서류 업로드 → 관세사 전송
  var sendToCustoms = async function(){
    if(!carRows.length){showToast('차량을 추가하세요','err');return;}
    if(!toEmail){showToast('관세사 이메일을 입력하세요','err');return;}
    setSending(true);setSendWarnings([]);
    try {
      var invId=await saveInvoiceToDb();
      if(!invId){setSending(false);return;}

      // 3) 추가서류 업로드 (쇼링)
      if(shoringFiles.length>0){
        var fd1=new FormData();fd1.append('docType','shoring_photo');
        var compressed2=await compressFiles(shoringFiles);
        compressed2.forEach(function(f){fd1.append('files',f);});
        await authFetch(API+'/invoices/'+invId+'/upload',{method:'POST',body:fd1});
      }

      // 4) 관세사 전송
      var sendResp = await authFetch(API+'/invoices/send-customs',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({invoiceId:invId,toEmail:toEmail})});
      var sendData = await sendResp.json();

      if(sendData.warnings && sendData.warnings.length>0 && !sendData.success){
        setSendWarnings(sendData.warnings);
        setSending(false);
        showToast('누락 서류가 있습니다. 확인 후 강제 전송하세요.','err');
        return;
      }
      // 백엔드가 success:false 또는 HTTP 4xx/5xx로 응답한 경우 (예: 첨부 용량 초과, SMTP 에러)
      if(!sendResp.ok || sendData.success===false || sendData.error){
        setSending(false);
        showToast(sendData.error||'전송 실패','err');
        return;
      }
      setSending(false);setSent(true);setInvoiceStatus('sent');
      authFetch(API+'/invoices/'+invId+'/status',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({invoice_status:'sent'})}).catch(function(){});
      try{sessionStorage.removeItem('autodoc_invoice_temp')}catch(e){}
      showToast('✅ 관세사 전송 완료! (첨부 '+sendData.attachments+'건) → 면장신청 자동진입');
    } catch(err){
      setSending(false);showToast('전송 실패: '+(err.message||'서버오류'),'err');
    }
  };

  // forceSend — utils/invoiceSubmit.js 로 분리 (Phase 2 시동, 2026-05-08)
  // saveDraft / sendToCustoms 는 saveInvoiceToDb helper 30+ state 결합 → 옵션 A 진입 시 처리 (백로그)
  var forceSend = function() {
    return _forceSendUtil({
      savedInvoiceId: savedInvoiceId, toEmail: toEmail,
      setSending: setSending, setSent: setSent, setSendWarnings: setSendWarnings,
      showToast: showToast
    });
  };

  // ─── ctx 객체 (자식 컴포넌트로 전달할 모든 상태/핸들러) ───
  var c = {
    ls,is,is2,carRows,currency,curSymbol,totalAmt,totalFreightInv,totalInsuranceInv,
    totalWeight,grandTotal,toInvCur,
    ssMode,setSsMode,ssLoading,ssResult,setSsResult,ssImageUrl,setSsImageUrl,handleScreenshot,handleTextPaste,
    ssPinned,setSsPinned,ssEdit,setSsEdit,ssPreview,setSsPreview,ssZoom,setSsZoom,ssDrag,setSsDrag,
    buyers,setSelBuyer,setConsignee,setContainerNo,setSealNo,setBkNo,setEntryPoint,setVesselName,
    setToPort,setFromPort,showToast,
    invoiceNo,setInvoiceNo,invoiceDate,setInvoiceDate,consignee,selBuyer,vesselName,fromPort,toPort,
    departDate,setDepartDate,bkNo,containerNo,sealNo,entryPoint,
    CURRENCIES,INCOTERMS,setCurrency,setIncoterms,rateEditing,setRateEditing,
    exchangeRate,setExchangeRate,rateLoading,fetchRate,incoterms,
    freight,setFreight,freightCurrency,setFreightCurrency,usdToKrw,
    insurance,setInsurance,insuranceCurrency,setInsuranceCurrency,
    resetForm,addRow,removeRow,updateRow,onCarSelect,
    carSearch,setCarSearch,doCarSearch,carSearchResults,setCarSearchResults,
    carSearchShow,setCarSearchShow,shippingType,setShippingType,
    shoringFiles,setShoringFiles,handleFilesAdded,uploadedDocs,setUploadedDocs,cars,carDocs,setCarDocs,shipCo,signatureUrl,
    savedList,savedInvoiceId,setSavedInvoiceId,loadInvoice,deleteDraft,
    invoiceStatus,toEmail,setToEmail,savingDraft,sending,sent,sendWarnings,
    confirmAction,setConfirmAction,saveDraft,printInvoice,sendToCustoms,forceSend
  };

  return { c: c, showCompanyFilter: showCompanyFilter, filterCompanyId: filterCompanyId, setFilterCompanyId: setFilterCompanyId, saveInvoiceDraft: saveInvoiceDraft };
}
